/**
 * web/src/components/ui/tooltip.tsx
 * Radix Tooltip with token-styled content. Used for icon-only buttons,
 * help hints, and any case where a native `title` attribute can't carry
 * the richness the design wants.
 *
 * Wrap the app's Tooltip-using subtree in <TooltipProvider> once (typically
 * in AppShell). Each <Tooltip> can then declare its own delay/duration via
 * Radix props.
 */
import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementRef } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../lib/cn';

/** Provider context — wraps the tooltip-using subtree. Mount once at the app root. */
export const TooltipProvider = TooltipPrimitive.Provider;

/** Root element controlling open state for a single tooltip. */
export const Tooltip = TooltipPrimitive.Root;

/** Element that displays the tooltip on hover/focus. Accepts asChild. */
export const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * Content bubble — black bg, white text, 12px font, 6px side offset,
 * shadow-pop, fade-in on open. Portals to document.body so it escapes
 * parent overflow/transform.
 */
export const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 rounded-md bg-text text-white px-2 py-1 text-[12px] shadow-pop',
        'data-[state=open]:animate-[fade-in_150ms_ease-out]',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = 'TooltipContent';
